import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useGetPromoCodesQuery, useDeletePromoCodeMutation } from '../../../store/api/adminApi';
import PageLoader from '../../../components/PageLoader';
import ActionButtons from '../../../components/ActionButtons';
import Pagination from '../../../components/Pagination';
import { showSuccess, showError } from '../../../utils/toast';

const formatDate = (dateString: string | null | undefined): string => {
  if (!dateString) return '-';
  const date = new Date(dateString);
  const months = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
  return `${date.getDate().toString().padStart(2, '0')} ${months[date.getMonth()]}, ${date.getFullYear()}`;
};

const AdminPromoCodes: React.FC = () => {
  const navigate = useNavigate();
  const [search, setSearch] = useState('');
  const [page, setPage] = useState(1);

  const { data, isLoading, error, refetch } = useGetPromoCodesQuery({ search, page });
  const [deletePromoCode] = useDeletePromoCodeMutation();

  useEffect(() => {
    setPage(1);
  }, [search]);

  const handleDelete = async (id: number) => {
    try {
      const result = await deletePromoCode(id).unwrap();
      showSuccess(result.message || 'Promo code deleted successfully!');
      refetch();
    } catch (err: any) {
      showError(err?.data?.message || 'Failed to delete promo code.');
    }
  };

  if (isLoading) return <PageLoader />;
  if (error) return <div className="alert alert-danger m-5">Error loading promo codes</div>;

  const codes = data?.data || { data: [], current_page: 1, last_page: 1, total: 0, per_page: 15 };

  return (
    <div className="app-main flex-column flex-row-fluid" id="kt_app_main">
      <div className="d-flex flex-column flex-column-fluid">
        <div id="kt_app_toolbar" className="app-toolbar py-3 py-lg-6">
          <div id="kt_app_toolbar_container" className="app-container container-xxl d-flex flex-stack">
            <div className="page-title d-flex flex-column justify-content-center flex-wrap me-3">
              <h1 className="page-heading d-flex text-dark fw-bold fs-3 flex-column justify-content-center my-0">Promo Codes</h1>
              <ul className="breadcrumb breadcrumb-separatorless fw-semibold fs-7 my-0 pt-1">
                <li className="breadcrumb-item text-muted"><span className="text-muted text-hover-primary">Home</span></li>
                <li className="breadcrumb-item"><span className="bullet bg-gray-400 w-5px h-2px"></span></li>
                <li className="breadcrumb-item text-muted">Promo Codes</li>
              </ul>
            </div>
          </div>
        </div>

        <div id="kt_app_content" className="app-content flex-column-fluid">
          <div id="kt_app_content_container" className="app-container container-xxl">
            <div className="card">
              <div className="card-header border-0 pt-6">
                <div className="card-title">
                  <form onSubmit={(e) => e.preventDefault()}>
                    <div className="d-flex align-items-center position-relative my-1">
                      <span className="svg-icon svg-icon-1 position-absolute ms-6">
                        <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                          <rect opacity="0.5" x="17.0365" y="15.1223" width="8.15546" height="2" rx="1" transform="rotate(45 17.0365 15.1223)" fill="currentColor" />
                          <path d="M11 19C6.55556 19 3 15.4444 3 11C3 6.55556 6.55556 3 11 3C15.4444 3 19 6.55556 19 11C19 15.4444 15.4444 19 11 19ZM11 5C7.53333 5 5 7.53333 5 11C5 14.4667 7.53333 17 11 17C14.4667 17 17 14.4667 17 11C17 7.53333 14.4667 5 11 5Z" fill="currentColor" />
                        </svg>
                      </span>
                      <input
                        type="text"
                        className="form-control form-control-solid w-350px ps-15"
                        placeholder="Search by title or code..."
                        value={search}
                        onChange={(e) => setSearch(e.target.value)}
                      />
                      {search && (
                        <button type="button" onClick={() => setSearch('')} className="btn btn-sm btn-light-primary ms-3">
                          Clear
                        </button>
                      )}
                    </div>
                  </form>
                </div>
                <div className="card-toolbar">
                  <button type="button" className="btn btn-base" onClick={() => navigate('/admin/promo-codes/add')}>
                    <span className="svg-icon svg-icon-2">
                      <svg width="24" height="24" viewBox="0 0 24 24" fill="none" xmlns="http://www.w3.org/2000/svg">
                        <rect opacity="0.5" x="11.364" y="20.364" width="16" height="2" rx="1" transform="rotate(-90 11.364 20.364)" fill="currentColor" />
                        <rect x="4.36396" y="11.364" width="16" height="2" rx="1" fill="currentColor" />
                      </svg>
                    </span>
                    Add Promo Code
                  </button>
                </div>
              </div>

              <div className="card-body pt-0">
                <div className="table-responsive">
                  <table className="table align-middle table-row-dashed fs-6 gy-5">
                    <thead>
                      <tr className="text-start text-gray-400 fw-bold fs-7 text-uppercase gs-0">
                        <th>#</th>
                        <th>Title</th>
                        <th>Code</th>
                        <th>Discount</th>
                        <th>Min / Max</th>
                        <th>User Type</th>
                        <th>Expiry</th>
                        <th>Status</th>
                        <th>Actions</th>
                      </tr>
                    </thead>
                    <tbody className="fw-semibold text-gray-600">
                      {codes.data.length === 0 ? (
                        <tr>
                          <td colSpan={9} className="text-center py-10">
                            <span className="text-muted">No promo codes found</span>
                          </td>
                        </tr>
                      ) : (
                        codes.data.map((promo: any, index: number) => (
                          <tr key={promo.id}>
                            <td>{(codes.current_page - 1) * codes.per_page + index + 1}</td>
                            <td>
                              <div className="fw-bold text-gray-800">{promo.title}</div>
                              {promo.title_ar && <div className="text-muted fs-7">{promo.title_ar}</div>}
                            </td>
                            <td>
                              <span className="badge badge-light-primary fs-7 fw-bold">{promo.code}</span>
                            </td>
                            <td>
                              {promo.discount_type === 'percentage'
                                ? `${promo.discount_value}%`
                                : `${promo.discount_value} (fixed)`}
                            </td>
                            <td>
                              {promo.min_amount ?? '-'} / {promo.max_amount ?? '-'}
                            </td>
                            <td>
                              <span className="badge badge-light-info text-capitalize">{promo.user_type}</span>
                            </td>
                            <td>
                              {promo.expiry_type === 'date'
                                ? formatDate(promo.expiry_date)
                                : promo.expiry_type === 'count'
                                ? `${promo.expiry_count} uses`
                                : '-'}
                            </td>
                            <td>
                              <span className={`badge ${promo.status === 'active' ? 'badge-light-success' : 'badge-light-danger'}`}>
                                {promo.status ?? 'active'}
                              </span>
                            </td>
                            <td>
                              <ActionButtons
                                onView={() => navigate(`/admin/promo-codes/${promo.id}`)}
                                onEdit={() => navigate(`/admin/promo-codes/${promo.id}/edit`)}
                                onDelete={() => handleDelete(promo.id)}
                              />
                            </td>
                          </tr>
                        ))
                      )}
                    </tbody>
                  </table>
                </div>
                {codes.data.length > 0 && (
                  <div className="card-footer border-0 pt-6">
                    <Pagination
                      currentPage={codes.current_page}
                      lastPage={codes.last_page}
                      total={codes.total}
                      perPage={codes.per_page}
                      onPageChange={(newPage) => setPage(newPage)}
                      showTotal={true}
                    />
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPromoCodes;
